import axios, { AxiosResponse, AxiosError } from "axios";
import { triggerGlobalNotifications } from "../../context/ErrorBridge";
import { bugsnagClient } from "../../config/bugsnag";
import { activeAccountId } from "../../store/accountStore";

const axiosBaseConfig = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_API_PROTOCOL}://${process.env.REACT_APP_BACKEND_API_DOMAIN}/`,
  withCredentials: true,
});

// The payment endpoints are REST: without the account header a teammate's
// top-up would be credited to their own account, not the one on screen.
axiosBaseConfig.interceptors.request.use((config) => {
  const accountId = activeAccountId();
  if (accountId) config.headers.set("x-account-id", String(accountId));
  return config;
});

/**
 * Centralized API error handler
 * @param error AxiosError object
 * @param context Optional context string for logging (e.g. 'checkAuth')
 */
export const handleApiError = (error: any, context = "API"): void => {
  const err = error as AxiosError;

  if (err.response) {
    const status = err.response.status;
    const data = err.response.data;

    bugsnagClient.notify(err, (event) => {
      event.severity = status >= 500 ? "error" : "warning";
      event.addMetadata("api", {
        status,
        endpoint: err.config?.url,
        method: err.config?.method,
        context,
        response: data,
      });
    });

    switch (status) {
      case 400:
        console.error(`[${context}] Bad Request`, data);
        triggerGlobalNotifications({ type: context, data });
        break;
      case 401:
        console.warn(`[${context}] Unauthorized`);
        // triggerGlobalNotifications("You are not authorized. Please log in.");
        break;
      case 403:
        console.warn(`[${context}] Forbidden`);
        triggerGlobalNotifications({ type: context, data });
        break;
      case 404:
        console.warn(`[${context}] Not Found`);
        triggerGlobalNotifications({ type: context, data });
        break;
      case 409:
        console.warn(`[${context}] Conflict`, data);
        triggerGlobalNotifications({ type: context, data });
        break;
      case 500:
        console.error(`[${context}] Server Error`, data);
        triggerGlobalNotifications({ type: context, data });
        break;
      default:
        console.error(`[${context}] Unhandled status code ${status}`, data);
        triggerGlobalNotifications({ type: context, data });
    }
  } else if (err.request) {
    bugsnagClient.notify(err, (event) => {
      event.severity = "warning";
      event.addMetadata("api", { context, phase: "no-response" });
    });
    console.error(`[${context}] No response received`, err.request);
    triggerGlobalNotifications(`${context}`);
  } else {
    bugsnagClient.notify(err, (event) => {
      event.severity = "warning";
      event.addMetadata("api", { context, phase: "setup" });
    });
    console.error(`[${context}] Error setting up request`, err.message);
    triggerGlobalNotifications(`${context}`);
  }
};

export const sendRecoveryEmail = async (
  email: string,
): Promise<AxiosResponse | null> => {
  try {
    return await axiosBaseConfig.post("auth/recover-password", { email });
  } catch (error) {
    handleApiError(error, "sendRecoveryEmail");
    return null;
  }
};

export const confirmPasswordUpdate = async (
  password: string,
  code: string,
): Promise<AxiosResponse | null> => {
  try {
    return await axiosBaseConfig.post("auth/reset-password", {
      password,
      code,
    });
  } catch (error) {
    handleApiError(error, "confirmPasswordUpdate");
    return null;
  }
};

export const checkAuth = async (): Promise<boolean> => {
  try {
    const response = await axiosBaseConfig.get("auth/check");
    return response.status === 200;
  } catch (error) {
    handleApiError(error, "checkAuth");
    return false;
  }
};

export const logout = async (): Promise<boolean> => {
  try {
    const response = await axiosBaseConfig.get("auth/logout");
    return response.status === 200;
  } catch (error) {
    handleApiError(error, "logout");
    return false;
  }
};

export const register = async (payload: any): Promise<boolean> => {
  try {
    const response = await axiosBaseConfig("auth/register", {
      method: "POST",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
      data: payload,
      withCredentials: true,
    });

    return response.status === 200 || response.status === 201;
  } catch (error) {
    handleApiError(error, "register");
    return false;
  }
};

export const confirmEmail = async (payload: any): Promise<boolean> => {
  try {
    const response = await axiosBaseConfig("auth/confirm-email", {
      method: "POST",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
      data: payload,
      withCredentials: true,
    });

    return response.status === 200 || response.status === 201;
  } catch (error) {
    handleApiError(error, "register");
    return false;
  }
};

export const login = async (payload: any): Promise<boolean> => {
  try {
    const response = await axiosBaseConfig("auth/login", {
      method: "POST",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
      data: payload,
      withCredentials: true,
    });

    return response.status === 200 || response.status === 201;
  } catch (error) {
    handleApiError(error, "register");
    return false;
  }
};

export default axiosBaseConfig;
